import React, { useEffect, useState, useCallback, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  ButtonBase,
  Container,
  alpha,
  Fab,
  SelectChangeEvent,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Vehicle, VehicleStatus, VehicleUsageType } from '../../types/vehicle';
import { User, Fleet } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import vehicleService from '../../services/vehicleService';
import { userService } from '../../services/userService';
import { fleetService } from '../../services/fleetService';
import InfoTooltip from '../common/InfoTooltip';
import VehicleFilter from './VehicleFilter';

interface VehicleListProps {
  onAddVehicle: () => void;
  onEditVehicle: (vehicle: Vehicle) => void;
  onDeleteVehicle: (vehicleId: string) => void;
  onViewDetails: (vehicle: Vehicle, tab?: number) => void;
  onRefreshList?: () => void;
}

const VehicleList = ({
  onAddVehicle,
  onEditVehicle,
  onDeleteVehicle,
  onViewDetails,
  onRefreshList
}: VehicleListProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignedUsers, setAssignedUsers] = useState<Record<string, User>>({});
  const [fleets, setFleets] = useState<Record<string, Fleet>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [usageTypeFilter, setUsageTypeFilter] = useState<VehicleUsageType | null>(null);
  const [fleetFilter, setFleetFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalVehicles, setTotalVehicles] = useState(0);

  const loadVehicles = useCallback(async (): Promise<void> => {
    if (!user?.organizationId) return;
    try {
      setLoading(true);
      const { vehicles: vehiclesList, total } = await vehicleService.getOrganizationVehicles(
        user.organizationId,
        {
          page,
          limit: rowsPerPage,
          status: statusFilter !== 'all' ? statusFilter as VehicleStatus : undefined,
          usageType: usageTypeFilter,
          fleetId: fleetFilter
        }
      );
      setVehicles(vehiclesList);
      setTotalVehicles(total);
      
      // Get unique user IDs
      const uniqueUserIds = Array.from(new Set(
        vehiclesList
          .filter(v => v.currentAssignment?.userId)
          .map(v => v.currentAssignment!.userId)
      ));

      const userPromises = uniqueUserIds.map(async userId => {
        try {
          const user = await userService.getUserById(userId);
          return { userId, user };
        } catch (error) {
          console.error(`Error loading user ${userId}:`, error);
          return { userId, user: null };
        }
      });

      const users = await Promise.all(userPromises);
      const usersMap = users.reduce((acc, { userId, user }) => {
        if (user) acc[userId] = user;
        return acc;
      }, {} as Record<string, User>);

      setAssignedUsers(usersMap);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId, page, rowsPerPage, statusFilter, usageTypeFilter, fleetFilter]);

  const handleChangePage = useCallback((event: unknown, newPage: number): void => {
    setPage(newPage);
    loadVehicles();
  }, [loadVehicles]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadVehicles();
  }, [loadVehicles]);

  const handleStatusFilterChange = useCallback((event: SelectChangeEvent<string>): void => {
    setStatusFilter(event.target.value);
    setPage(0);
    loadVehicles();
  }, [loadVehicles]);

  const getLabelDisplayedRows = useCallback(({ from, to, count }: { from: number; to: number; count: number }): string => {
    return t('common.pagination.displayedRows', { 
      from: from + 1,
      to: Math.min(to + 1, count),
      count 
    });
  }, [t]);

  // Effects
  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  useEffect(() => {
    if (onRefreshList) {
      loadVehicles();
    }
  }, [onRefreshList, loadVehicles]);

  // Charger les flottes
  useEffect(() => {
    const loadFleets = async () => {
      if (!user?.organizationId) return;
      try {
        const fleetsList = await fleetService.getFleets(user.organizationId);
        const fleetsMap = fleetsList.reduce((acc, fleet) => {
          acc[fleet.id] = fleet;
          return acc;
        }, {} as Record<string, Fleet>);
        setFleets(fleetsMap);
      } catch (err) {
        console.error('Error loading fleets:', err);
      }
    };
    
    loadFleets();
  }, [user?.organizationId]);

  // Utility functions
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    return timestamp.toDate().toLocaleDateString(i18n.language);
  };

  const formatPower = (vehicle: Vehicle): string => {
    const hp = vehicle.enginePower;
    const kw = vehicle.technicalDetails?.enginePowerKW;
    
    if (kw) {
      return `${hp} HP / ${kw} kW`;
    }
    return `${hp} HP`;
  };

  const getStatusColor = (status: VehicleStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'sold':
        return 'error';
      default:
        return 'default';
    }
  };

  const exportToCSV = (): void => {
    const DELIMITER = ';';
    const headers = [
      t('vehicle.export.headers.make'),
      t('vehicle.export.headers.model'),
      t('vehicle.export.headers.year'),
      t('vehicle.export.headers.licensePlate'),
      t('vehicle.export.headers.status'),
      t('vehicle.export.headers.assignedTo'),
      t('vehicle.export.headers.currentMileage'),
      t('vehicle.export.headers.enginePower'),
      t('vehicle.export.headers.fuelType'),
      t('vehicle.export.headers.insuranceProvider'),
      t('vehicle.export.headers.policyNumber'),
      t('vehicle.export.headers.insuranceExpiry'),
      t('vehicle.export.headers.lastInspection'),
      t('vehicle.export.headers.nextInspection'),
    ].join(DELIMITER);

    const rows = vehicles.map((vehicle: Vehicle) => {
      const assignedUser = vehicle.currentAssignment?.userId ?
        assignedUsers[vehicle.currentAssignment.userId] : null;

      const assignedUserName = assignedUser ?
        (assignedUser.displayName ||
         `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim() ||
         assignedUser.email) :
        t('vehicle.assignment.unassign');

      const fields = [
        vehicle.make,
        vehicle.model,
        vehicle.year,
        vehicle.licensePlate,
        t(`vehicle.status.${vehicle.status}`),
        assignedUserName,
        vehicle.currentMileage,
        formatPower(vehicle),
        t(`vehicle.technical.fuelType.${vehicle.fuelType}`),
        vehicle.insurance.provider,
        vehicle.insurance.policyNumber,
        formatDate(vehicle.insurance.expirationDate),
        formatDate(vehicle.technicalInspection.lastInspectionDate),
        formatDate(vehicle.technicalInspection.nextInspectionDate),
      ];

      return fields.map((field: string | number) => {
        const stringField = String(field || '');
        return stringField.includes(DELIMITER) || stringField.includes('"') || stringField.includes('\n') 
          ? `"${stringField.replace(/"/g, '""')}"` 
          : stringField;
      }).join(DELIMITER);
    });

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${t('vehicle.export.filename')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render functions
  const renderMobileView = (): JSX.Element => {
    return (
      <Container maxWidth="sm" disableGutters>
        <Grid container spacing={2.5} sx={{ px: 1, py: 1.5 }}>
          {vehicles.map((vehicle) => (
            <Grid item xs={12} key={vehicle.id}>
              <Card 
                elevation={2}
                component={ButtonBase}
                onClick={() => onViewDetails(vehicle)}
                focusRipple
                TouchRippleProps={{
                  center: false,
                  classes: {
                    ripple: 'ripple-light'
                  }
                }}
                sx={{ 
                  width: '100%',
                  display: 'block',
                  textAlign: 'left',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: 1,
                  overflow: 'hidden',
                  borderLeft: (theme) => `6px solid ${
                    vehicle.status === 'active' ? alpha(theme.palette.success.main, 0.7) :
                    vehicle.status === 'inactive' ? alpha(theme.palette.warning.main, 0.7) :
                    vehicle.status === 'sold' ? alpha(theme.palette.error.main, 0.7) :
                    theme.palette.grey[300]
                  }`,
                  '&:active': {
                    transform: 'scale(0.98)',
                    backgroundColor: (theme) => alpha(theme.palette.action.selected, 0.12)
                  },
                  '&:hover': {
                    boxShadow: 3,
                    borderLeftColor: (theme) => 
                      vehicle.status === 'active' ? theme.palette.success.main :
                      vehicle.status === 'inactive' ? theme.palette.warning.main :
                      vehicle.status === 'sold' ? theme.palette.error.main :
                      theme.palette.primary.main
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.04)',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  },
                  '&:active::after': {
                    opacity: 1
                  },
                  '& .ripple-light': {
                    color: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <CardContent 
                  sx={{ 
                    py: 3,
                    position: 'relative', 
                    pr: 3,
                    pl: 3,
                    '&:hover .arrow-icon': {
                      transform: 'translate(4px, -50%)',
                      opacity: 0.6
                    },
                    '@media (hover: none)': {
                      '.arrow-icon': {
                        opacity: 0.6
                      }
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                    <Typography variant="h6" sx={{ pr: 2, lineHeight: 1.3 }}>
                      {vehicle.make} {vehicle.model} ({vehicle.year})
                    </Typography>
                    <Chip
                      label={t(`vehicle.status.${vehicle.status}`)}
                      color={getStatusColor(vehicle.status)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100 }}>
                        {t('vehicle.table.headers.licensePlate')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {vehicle.licensePlate}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100 }}>
                        {t('vehicle.usage.title')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {vehicle.usageType ? t(`vehicle.usage.${vehicle.usageType}`) : '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100 }}>
                        {t('vehicle.table.headers.assignedTo')}:
                      </Typography>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {vehicle.currentAssignment?.userId && assignedUsers[vehicle.currentAssignment.userId] ? (
                            <>
                              {assignedUsers[vehicle.currentAssignment.userId].displayName ||
                               `${assignedUsers[vehicle.currentAssignment.userId].firstName || ''} ${assignedUsers[vehicle.currentAssignment.userId].lastName || ''}`.trim() ||
                               assignedUsers[vehicle.currentAssignment.userId].email}
                              <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                                {t('common.since')}: {vehicle.currentAssignment.assignedDate.toDate().toLocaleDateString()}
                              </Typography>
                            </>
                          ) : (
                            t('vehicle.assignment.unassign')
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100 }}>
                        {t('vehicle.table.headers.currentMileage')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {vehicle.currentMileage.toLocaleString()} km
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 100 }}>
                        {t('vehicle.technical.power')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatPower(vehicle)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Box 
          display="flex" 
          flexDirection={isMobile ? 'column' : 'row'} 
          justifyContent="space-between" 
          alignItems={isMobile ? 'stretch' : 'center'} 
          gap={2}
        >
          <Stack 
            direction={isMobile ? "column" : "row"} 
            alignItems={isMobile ? "flex-start" : "center"}
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" component="h2" sx={{ mr: 1 }}>
                {t('vehicle.list')}
              </Typography>
              <InfoTooltip text={t('vehicle.info')} sx={{ mt: isMobile ? 0.5 : 0 }} />
            </Box>
          </Stack>
          {!isMobile && (
            <Box 
              display="flex" 
              gap={2} 
              flexDirection="row"
              sx={{
                '& .MuiButton-root': {
                  justifyContent: 'flex-start'
                }
              }}
            >
              <Button
                variant="contained"
                startIcon={<CarIcon />}
                onClick={onAddVehicle}
                color="primary"
              >
                {t('vehicle.addVehicle')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportToCSV}
              >
                {t('vehicle.export.csv')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: isMobile ? '100%' : 200 }}>
          <InputLabel>{t('vehicle.status.filter')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('vehicle.status.filter')}
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="all">{t('vehicle.status.allStatuses')}</MenuItem>
            <MenuItem value="active">{t('vehicle.status.active')}</MenuItem>
            <MenuItem value="inactive">{t('vehicle.status.inactive')}</MenuItem>
            <MenuItem value="sold">{t('vehicle.status.sold')}</MenuItem>
          </Select>
        </FormControl>
        <VehicleFilter
          vehicles={[]}
          fleets={Object.values(fleets)}
          selectedVehicle={null}
          selectedUsageType={usageTypeFilter}
          selectedFleet={fleetFilter}
          onVehicleChange={() => {}}
          onUsageTypeChange={(usageType) => {
            setUsageTypeFilter(usageType);
            setPage(0);
            loadVehicles();
          }}
          onFleetChange={(fleetId) => {
            setFleetFilter(fleetId);
            setPage(0);
            loadVehicles();
          }}
        />
      </Stack>

      <Box sx={{ width: '100%', mb: 2 }}>
        <TablePagination
          component="div"
          count={totalVehicles}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('common.pagination.rowsPerPage')}
          labelDisplayedRows={getLabelDisplayedRows}
        />
      </Box>

      {isMobile ? (
        <>
          {renderMobileView()}
          <Box sx={{ 
            position: 'fixed', 
            right: 16, 
            bottom: 16, 
            gap: 1.5, 
            display: 'flex', 
            flexDirection: 'column',
            '& .MuiFab-root': {
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)'
              },
              '&:active': {
                transform: 'scale(0.95)'
              }
            }
          }}>
            <Fab
              color="primary"
              onClick={onAddVehicle}
              aria-label={t('vehicle.addVehicle')}
            >
              <AddIcon />
            </Fab>
            <Fab
              color="default"
              onClick={exportToCSV}
              aria-label={t('vehicle.export.csv')}
            >
              <FileDownloadIcon />
            </Fab>
          </Box>
        </>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('vehicle.table.headers.vehicle')}</TableCell>
                <TableCell>{t('vehicle.table.headers.assignedTo')}</TableCell>
                <TableCell>{t('vehicle.table.headers.licensePlate')}</TableCell>
                <TableCell>{t('vehicle.table.headers.status')}</TableCell>
                <TableCell>{t('vehicle.usage.title')}</TableCell>
                <TableCell>{t('vehicle.fleet')}</TableCell>
                <TableCell>{t('vehicle.table.headers.technicalDetails')}</TableCell>
                <TableCell>{t('vehicle.table.headers.currentMileage')}</TableCell>
                <TableCell>{t('vehicle.table.headers.lastInspection')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((vehicle) => (
                <TableRow 
                  key={vehicle.id} 
                  onClick={() => onViewDetails(vehicle)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1">
                        {vehicle.make} {vehicle.model} ({vehicle.year})
                      </Typography>
                      {vehicle.technicalDetails?.manufacturerDetails?.trim && (
                        <Typography variant="caption" color="textSecondary">
                          {vehicle.technicalDetails.manufacturerDetails.trim}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {vehicle.currentAssignment?.userId && assignedUsers[vehicle.currentAssignment.userId] ? (
                      <Box>
                        <Typography variant="body2">
                          {assignedUsers[vehicle.currentAssignment.userId].displayName ||
                           `${assignedUsers[vehicle.currentAssignment.userId].firstName || ''} ${assignedUsers[vehicle.currentAssignment.userId].lastName || ''}`.trim() ||
                           assignedUsers[vehicle.currentAssignment.userId].email}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('common.since')}: {vehicle.currentAssignment.assignedDate.toDate().toLocaleDateString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t('vehicle.assignment.unassign')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{vehicle.licensePlate}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`vehicle.status.${vehicle.status}`)}
                      color={getStatusColor(vehicle.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {vehicle.usageType ? t(`vehicle.usage.${vehicle.usageType}`) : '-'}
                  </TableCell>
                  <TableCell>
                    {vehicle.fleetId ? fleets[vehicle.fleetId]?.name : t('vehicle.noFleet')}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        <SpeedIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {formatPower(vehicle)}
                      </Typography>
                      {vehicle.technicalDetails?.engineSize && (
                        <Box mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            {vehicle.technicalDetails.engineSize} cc
                          </Typography>
                        </Box>
                      )}
                      {vehicle.technicalDetails?.co2Emissions && (
                        <Box mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            CO₂: {vehicle.technicalDetails.co2Emissions} g/km
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {vehicle.currentMileage.toLocaleString()} km
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {vehicle.technicalInspection.lastInspectionDate?.toDate().toLocaleDateString()}
                      </Typography>
                      {vehicle.technicalInspection.nextInspectionDate && (
                        <Typography variant="caption" color="textSecondary">
                          {t('common.next')}: {vehicle.technicalInspection.nextInspectionDate.toDate().toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default VehicleList;
